import { useState, useEffect, useCallback } from 'react';
import type { TeamId } from '../types/game';
import { TEAM_NAMES, TEAM_COLORS } from '../types/game';
import { Icon } from './Icon';

interface RouletteSelectorProps {
  teamOrder: TeamId[];
  onSpin: () => void;
  onStartRound: () => void;
  hasQuestion: boolean;
}

export const RouletteSelector = ({
  teamOrder,
  onSpin,
  onStartRound,
  hasQuestion
}: RouletteSelectorProps) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [displayOrder, setDisplayOrder] = useState<TeamId[]>([]);
  const [showResult, setShowResult] = useState(false);

  const hasOrder = teamOrder.length > 0;

  useEffect(() => {
    if (hasOrder && !isSpinning) {
      setDisplayOrder(teamOrder);
      setShowResult(true);
    }
  }, [hasOrder, teamOrder, isSpinning]);

  const handleSpin = useCallback(() => {
    setIsSpinning(true);
    setShowResult(false);

    const teams: TeamId[] = ['A', 'B', 'C'];
    const interval = setInterval(() => {
      setDisplayOrder([...teams].sort(() => Math.random() - 0.5));
    }, 100);

    setTimeout(() => {
      clearInterval(interval);
      setIsSpinning(false);
      onSpin();
    }, 2000);
  }, [onSpin]);

  const handleStartRound = useCallback(() => {
    setShowResult(false);
    onStartRound();
  }, [onStartRound]);

  return (
    <div
      className="flex flex-col items-center justify-center p-8 relative w-full h-full"
    >

      <div className="text-center mb-12 relative z-10">
        <h2 className="text-3xl md:text-4xl font-black uppercase tracking-widest mb-4 gradient-text flex items-center gap-3">
          <Icon name="dice" size="3xl" /> Sorteo de Equipos
        </h2>
        <p className="text-xl text-muted">
          Orden en que participarán los equipos
        </p>
      </div>

      <div className="min-h-64 flex items-center justify-center mb-12 relative z-10">
        {isSpinning && (
          <div className="text-5xl font-black text-warning animate-pulse">
            ¡Sorteando!
          </div>
        )}

        {showResult && displayOrder.length > 0 ? (
          <div className="flex gap-12">
            {displayOrder.map((team, index) => (
              <div
                key={team}
                className="flex flex-col items-center gap-4 animate-bounce-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <span className="text-4xl font-black text-warning">
                  {index + 1}°
                </span>
                <div
                  className="w-36 h-36 rounded-2xl border-4 border-white flex items-center justify-center text-4xl font-black text-black"
                  style={{
                    background: TEAM_COLORS[team].bg,
                    boxShadow: `8px 8px 0px white`,
                  }}
                >
                  {TEAM_NAMES[team]}
                </div>
              </div>
            ))}
          </div>
        ) : !isSpinning ? (
          <div className="flex gap-8">
            {(['A', 'B', 'C'] as TeamId[]).map((t) => (
              <div
                key={t}
                className={`flex flex-col items-center ${isSpinning ? 'animate-bounce' : ''}`}
              >
                <div
                  className="w-36 h-36 rounded-2xl border-4 border-white flex items-center justify-center text-4xl font-black text-black"
                  style={{
                    background: TEAM_COLORS[t].bg,
                    boxShadow: `6px 6px 0px white`,
                  }}
                >
                  {TEAM_NAMES[t]}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex gap-4 justify-center relative z-10">
        {!hasOrder ? (
          <button
            className="btn btn-primary btn-xl"
            onClick={handleSpin}
            disabled={isSpinning}
          >
            {isSpinning ? <><Icon name="dice" /> Sorteando...</> : <><Icon name="sync" /> Sortear Equipos</>}
          </button>
        ) : (
          <>
            <button
              className="btn btn-outline btn-lg"
              onClick={handleSpin}
              disabled={isSpinning}
            >
              <Icon name="sync" /> Sortear de nuevo
            </button>
            <button
              className="btn btn-primary btn-xl"
              onClick={handleStartRound}
            >
              ▶ Comenzar Ronda
            </button>
          </>
        )}
      </div>

      {hasQuestion && (
        <div className="mt-8 px-6 py-3 rounded-xl bg-warning border-2 border-white text-black font-black uppercase shadow-[4px_4px_0_white] relative z-10">
          <Icon name="warning" /> Ya hay una pregunta cargada. Debes iniciar una nueva ronda primero.
        </div>
      )}
    </div>
  );
};
